import type { Session } from '@supabase/supabase-js'
import type { ReactNode } from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { adoptSessionFromUrl } from './adopt-session'
import { markHandOffTried, startHandOffIfNeeded } from './sso-hand-off'
import { supabase } from './supabase'

interface AuthState {
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
}

// Default context value so useAuth() NEVER throws even outside a provider — e.g. when AircargoHome is
// mounted bare as a Module-Federation remote inside the Navo24 host (WorkspaceApp has no AuthProvider).
const AuthCtx = createContext<AuthState>({ session: null, loading: true, signOut: async () => {} })

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const { pathname } = useLocation()

  useEffect(() => {
    let active = true
    // Adopt a cross-domain hand-off session (from the Navo24 host) BEFORE reading the session, so an
    // owner arriving via "Open AirCargo" lands authenticated instead of flashing /login. No-op when
    // there's no hand-off nonce in the URL.
    ;(async () => {
      // A load that arrives WITH a nonce is the hub's answer: record the attempt so a failed
      // redemption falls through to the normal login and never starts a second hand-off.
      if (window.location.hash.includes('sso=')) markHandOffTried()
      await adoptSessionFromUrl()
      const { data } = await supabase.auth.getSession()
      if (!active) return
      setSession(data.session)
      setLoading(false)
    })()
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  // Cross-domain SSO: a visitor signed in on the hub gets signed in here with no second login. Fires
  // once per tab session, only on the auth routes, only when the session state has settled empty
  // (see lib/sso-hand-off.ts for every guard). Re-evaluated on route change so a visitor who lands on
  // a public page and then clicks "Sign in" is covered; the marker keeps it to one attempt.
  // biome-ignore lint/correctness/useExhaustiveDependencies: pathname is the trigger on purpose
  useEffect(() => {
    startHandOffIfNeeded(session !== null, loading)
  }, [session, loading, pathname])

  return (
    <AuthCtx.Provider
      value={{ session, loading, signOut: async () => void (await supabase.auth.signOut()) }}
    >
      {children}
    </AuthCtx.Provider>
  )
}

export const useAuth = () => useContext(AuthCtx)
